<template>
  <div class="yk-message-detail">
    <div class="yk-message-detail__header">
      <div v-if="title.name || title.roomName" class="yk-message-detail__profile">
        <template v-if="source === 'person'">
          <span class="yk-message-detail__name">{{ title.name || '-' }}</span>
          <span class="yk-message-detail__tag">{{ title.attr || '-' }}</span>
        </template>
        <template v-else>
          <span class="yk-message-detail__name">{{ title.roomName || '-' }}</span>
        </template>
      </div>
      <div class="yk-message-detail__filters">
        <el-date-picker
          v-model="filterForm.timeRange"
          class="yk-message-detail__date"
          :clearable="false"
          :default-time="dateDefaultTime"
          :disabled-date="disabledDate"
          end-placeholder="结束日期"
          range-separator="~"
          :shortcuts="dateShortcuts"
          start-placeholder="开始日期"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
          @calendar-change="handleCalendarChange"
          @change="handleFilterChange"
          @visible-change="handlePickerVisibleChange"
        />
        <el-select
          v-model="filterForm.messageType"
          class="yk-message-detail__type"
          placeholder="全部类型"
          @change="handleFilterChange"
        >
          <el-option
            v-for="typeItem in messageTypeOptions"
            :key="typeItem.value"
            :label="typeItem.label"
            :value="typeItem.value"
          />
        </el-select>
        <el-input
          v-model.trim="filterForm.keyword"
          class="yk-message-detail__search"
          clearable
          placeholder="搜索会话消息"
          :prefix-icon="Search"
          @clear="handleKeywordClear"
        />
      </div>
    </div>
    <div class="yk-message-detail__content">
      <!-- 消息列表 -->
      <yk-message-core
        ref="messageCore"
        :filter="messageFilter"
        :params="messageCoreParams"
        :source="source"
      />
      <!-- 搜索列表 -->
      <div v-if="filterForm.keyword" class="content-search">
        <YkMessageSearch
          :filter="searchFilter"
          :params="messageCoreParams"
          :source="source"
          @select="handleSearchSelect"
        ></YkMessageSearch>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YkMessageDetail'
}
</script>

<script setup>
import { Search } from '@element-plus/icons-vue'
import { computed, reactive, ref, defineExpose, shallowRef } from 'vue'
import { formatTime } from '@xbb/xbb-utils'
import YkMessageCore from '../yk-message-core/index.vue'
import YkMessageSearch from '../yk-message-search/index.vue'
import { ENUM_MESSAGE_TYPE } from '../yk-message-core/enum.yk-message-core'

const MESSAGE_TYPE_OPTIONS = Object.values(ENUM_MESSAGE_TYPE).map((item) => ({
  label: item.memo,
  value: item.type
}))

const props = defineProps({
  source: {
    type: String,
    default: 'person'
  },
  params: {
    type: Object,
    default: () => ({
      // 个微
      wechatUserId: '',
      wechatId: '',
      loginUserId: '',
      // 群
      roomId: ''
    })
  },
  title: {
    type: Object,
    default: () => ({
      name: '',
      attr: '',
      roomName: ''
    })
  },
  extraParams: {
    type: Object,
    default: () => ({
      timeRange: []
    })
  }
})

const filterForm = reactive({
  timeRange: [],
  messageType: 0,
  keyword: ''
})
const messageTypeOptions = MESSAGE_TYPE_OPTIONS
const messageFilter = shallowRef({})
const dateDefaultTime = [new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]
const calendarStartDate = ref(null)
const messageCore = ref(null)

const getStartOfDay = (date) => {
  const startOfDay = new Date(date)
  startOfDay.setHours(0, 0, 0, 0)
  return startOfDay
}

const formatDateTime = (date) => formatTime(date, 'yyyy-MM-dd HH:mm:ss')

const isValidTimeValue = (value) => value !== '' && value !== null && value !== undefined

const normalizeTimeValue = (value) => {
  const timestamp = Number(value)
  if (Number.isNaN(timestamp)) return value
  return formatDateTime(new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp))
}

const getTimestamp = (date, endOfDay = false) => {
  if (!date) return undefined
  const dateNumber = Number(date)
  const timestamp = new Date(
    Number.isNaN(dateNumber) ? date : dateNumber < 100000000000 ? dateNumber * 1000 : dateNumber
  )
  if (Number.isNaN(timestamp.getTime())) return undefined
  endOfDay ? timestamp.setHours(23, 59, 59, 999) : timestamp.setHours(0, 0, 0, 0)
  return timestamp.getTime()
}

const getDefaultTimeRange = () => {
  const todayStart = getStartOfDay(new Date())
  const latestWeekStart = new Date(todayStart)
  latestWeekStart.setDate(latestWeekStart.getDate() - 6)
  return [formatDateTime(latestWeekStart), formatDateTime(new Date())]
}

const getInitialTimeRange = (val = null) => {
  const { timeRange } = val || props.extraParams || {}
  if (Array.isArray(timeRange) && timeRange.length === 2 && timeRange.every(isValidTimeValue)) {
    return timeRange.map((item) => normalizeTimeValue(item))
  }
  return getDefaultTimeRange()
}

const addMonths = (date, monthCount) => {
  const result = new Date(date)
  const day = result.getDate()
  result.setDate(1)
  result.setMonth(result.getMonth() + monthCount)
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()
  result.setDate(Math.min(day, lastDay))
  return result
}

const dateShortcuts = computed(() => {
  const createRange = (startDate, endDate) => [startDate, endDate]
  const todayStart = getStartOfDay(new Date())
  const todayEnd = new Date()
  const yesterdayStart = new Date(todayStart)
  yesterdayStart.setDate(yesterdayStart.getDate() - 1)
  const yesterdayEnd = new Date(todayStart.getTime() - 1000)
  const latestWeekStart = new Date(todayStart)
  latestWeekStart.setDate(latestWeekStart.getDate() - 6)
  const latestMonthStart = new Date(todayStart)
  latestMonthStart.setDate(latestMonthStart.getDate() - 29)

  return [
    {
      text: '今日',
      value: createRange(todayStart, todayEnd)
    },
    {
      text: '昨日',
      value: createRange(yesterdayStart, yesterdayEnd)
    },
    {
      text: '近7天',
      value: createRange(latestWeekStart, todayEnd)
    },
    {
      text: '近30天',
      value: createRange(latestMonthStart, todayEnd)
    }
  ]
})

const handlePickerVisibleChange = (visible) => {
  if (!visible) {
    calendarStartDate.value = null
  }
}

const handleCalendarChange = (dates) => {
  const [startDate, endDate] = dates || []
  calendarStartDate.value = startDate && !endDate ? new Date(startDate) : null
}

const disabledDate = (date) => {
  if (!calendarStartDate.value) return false
  return (
    date < addMonths(calendarStartDate.value, -12) || date > addMonths(calendarStartDate.value, 12)
  )
}

const searchFilter = computed(() => ({
  ...messageFilter.value,
  keyword: filterForm.keyword
}))

const messageCoreParams = computed(() => {
  const { loginUserId, wechatId, wechatUserId, roomId } = props.params || {}

  return {
    loginUserId: loginUserId || '',
    roomId: roomId || '',
    wechatId: wechatId || '',
    wechatUserId: wechatUserId || ''
  }
})

const handleKeywordClear = () => {
  filterForm.keyword = ''
}

const getMessageFilter = () => {
  const [startDate = '', endDate = ''] = filterForm.timeRange || []

  return {
    startTime: getTimestamp(startDate),
    endTime: getTimestamp(endDate, true),
    msgType: filterForm.messageType || undefined
  }
}

const updateMessageFilterQuietly = (filter) => {
  Object.keys(messageFilter.value).forEach((key) => {
    delete messageFilter.value[key]
  })
  Object.assign(messageFilter.value, filter)
}

const handleFilterChange = () => {
  messageFilter.value = getMessageFilter()
}

const handleSearchSelect = (messageItem) => {
  filterForm.keyword = ''
  if (messageCore.value && messageCore.value.loadTargetMessage) {
    messageCore.value.loadTargetMessage(messageItem, messageFilter.value)
  }
}

// 定位到指定消息位置
const locationTargetMessage = ({ messageItem = {}, timeRange = [] }) => {
  if (timeRange.length) {
    filterForm.timeRange = getInitialTimeRange({ timeRange })
  }

  if (messageItem.id) {
    updateMessageFilterQuietly(getMessageFilter())
    handleSearchSelect(messageItem)
    return
  }

  if (timeRange.length) {
    handleFilterChange()
  }
}

filterForm.timeRange = getInitialTimeRange()
handleFilterChange()

defineExpose({
  locationTargetMessage
})
</script>

<style lang="scss" scoped>
.yk-message-detail {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #ffffff;

  &__header {
    box-sizing: border-box;
    flex-shrink: 0;
    padding: 16px;
    border-bottom: 1px solid #e5e6eb;
  }

  &__profile {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  &__name {
    font-size: 16px;
    line-height: 22px;
    color: #323233;
  }

  &__tag {
    margin-left: 2px;
    font-size: 12px;
    line-height: 16px;
    color: #00c241;
  }

  &__filters {
    display: flex;
    align-items: center;

    > * + * {
      margin-left: 12px;
    }
  }

  &__date,
  &__search {
    width: 220px !important;
  }

  &__type {
    width: 104px;
    flex-shrink: 0;
  }

  &__content {
    position: relative;
    flex: 1;
    min-height: 0;
  }

  .content-search {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    background: #ffffff;
  }
}
</style>
